import { isFunction, isPlainObject, isString, trimEnd, trimStart } from 'lodash';
import { detectEnv } from './detect';
import guidanceImage from '@/assets/img/guidance.png';

type HiddenType = 'hidden' | 'msHidden' | 'webkitHidden';

let hidden: HiddenType;
let visibilityChange: string;

if (typeof document.hidden !== 'undefined') {
  hidden = 'hidden';
  visibilityChange = 'visibilitychange';
} else if (typeof document.msHidden !== 'undefined') {
  hidden = 'msHidden';
  visibilityChange = 'msvisibilitychange';
} else if (typeof document.webkitHidden !== 'undefined') {
  hidden = 'webkitHidden';
  visibilityChange = 'webkitvisibilitychange';
}

const isHidden = () => hidden && document[hidden];

export interface LaunchAppSettings {
  protocol: string | null;
  timeout?: number;
  applinks?: string | null;
  fallback?: string | null;
  onFaliure?: () => void;
}

export interface LaunchAppOptions {
  path?: string;
  params?: Record<string, any>;
  onFaliure?: () => void;
}

const defaultSettings = {
  timeout: 3000,
  protocol: null,
  fallback: null,
  applinks: null,
};

const stringify = (params?: Record<string, any>) => {
  if (params == null || !isPlainObject(params)) return '';

  const str = Object.keys(params)
    .map((key) => (params[key] != null ? `${key}=${params[key]}` : ''))
    .join('&');

  return str ? `?${str}` : '';
};

export default class LaunchApp {
  private settings: LaunchAppSettings;
  private overlay: HTMLDivElement | undefined;

  constructor(settings: LaunchAppSettings) {
    if (settings.protocol == null) {
      throw new Error('settings.protocol must be required');
    }

    this.settings = {
      ...defaultSettings,
      ...settings,
    };
  }

  private generateScheme(options?: LaunchAppOptions) {
    const { protocol } = this.settings;

    return `${protocol}://${trimStart(options?.path ?? '', '/')}${stringify(options?.params)}`;
  }

  private generateAppLinks(options?: LaunchAppOptions) {
    const { applinks } = this.settings;

    return `${trimEnd(applinks ?? '', '/')}/${trimStart(options?.path ?? '', '/')}${stringify(
      options?.params,
    )}`;
  }

  private toFallback() {
    const { fallback } = this.settings;

    if (fallback) {
      // 宜享花APP外使用，可以忽略该eslint规则
      // eslint-disable-next-line no-restricted-syntax
      window.location.href = fallback;
    }
  }

  public checkLaunched(onFaliure?: () => void) {
    const { timeout } = this.settings;
    const timer: TimeoutId = setTimeout(() => {
      if (!isHidden()) {
        // 优先open传入的失败回调
        if (isFunction(onFaliure)) {
          onFaliure.call(this);

          // 其次尝试初始化时传入的失败回调
        } else if (isFunction(this.settings.onFaliure)) {
          this.settings.onFaliure.call(this);

          // 兜底跳转fallback地址
        } else {
          this.toFallback();
        }
      }
    }, timeout);

    const clear = () => {
      if (isHidden()) {
        clearTimeout(timer);
      }

      if (visibilityChange != null) {
        document.removeEventListener(visibilityChange, clear);
      } else {
        window.removeEventListener('pagehide', clear);
      }
    };

    if (visibilityChange != null) {
      document.addEventListener(visibilityChange, clear);
    } else {
      window.addEventListener('pagehide', clear);
    }
  }

  private launchByLocation(options?: LaunchAppOptions) {
    const { protocol, host } = window.location;

    // 当applinks域名与当前域名不一致时才使用applinks跳转
    if (this.settings.applinks && !this.settings.applinks.startsWith(`${protocol}//${host}`)) {
      (window.top ?? window).location.href = this.generateAppLinks(options);
      return;
    }
    (window.top ?? window).location.href = this.generateScheme(options);
  }

  private showTips() {
    if (this.overlay) {
      this.overlay.style.display = 'block';
      return;
    }

    this.overlay = document.createElement('div');
    this.overlay.style.cssText = `
      padding-right: 36px;
      text-align: right;
      color: #fff;
      background-color: rgba(0, 0, 0, 0.7);
      font-size: 18px;
      line-height: 25px;
      position: fixed;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
    `;

    this.overlay.addEventListener('click', () => {
      if (this.overlay) {
        this.overlay.style.display = 'none';
      }
    });

    const img = document.createElement('img');
    img.src = guidanceImage;
    img.style.cssText = 'width: 98px; margin-bottom: 10px;';
    this.overlay.appendChild(img);

    const tips = document.createElement('p');
    tips.textContent = '点击右上角的按钮';
    this.overlay.appendChild(tips);

    const tips2 = document.createElement('p');
    tips2.textContent = '在浏览器中打开宜享花APP';
    this.overlay.appendChild(tips2);

    document.body.appendChild(this.overlay);
  }

  public open(options?: string | LaunchAppOptions) {
    if (isString(options)) {
      options = {
        path: options,
      };
    }

    if (detectEnv.wechat) {
      this.showTips();
      return;
    }

    this.launchByLocation(options);
    this.checkLaunched(options?.onFaliure);
  }
}
